import styled, { css } from 'styled-components';
import { Paragraph } from 'src/components/common/typography/Text';
import { PimImage } from 'src/components/common/PimImage';
import { media } from 'src/styles/designs';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { Directus_Files } from 'src/graphql/schema.graphql';
import { ProductLogo } from 'src/modules/products/components/ProductLogo';
import { Button } from 'src/components/common/Button';
import { TrackedLink } from 'src/components/TrackedLink';
import { route } from 'src/services/route';
import { useTranslate } from 'react-t7e';
import { dirRTL } from 'src/styles/theme';

export interface ProductPageSctSectionItemProps {
  name?: string;
  code: string;
  tagline?: string;
  description?: string;
  image?: Directus_Files;
  singular?: boolean;
}

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 335px;
  margin-bottom: 16px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 335px;
    height: 335px;
  }
`;

const ImageInnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 340px;
`;

const Container = styled.div<{ $singular?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.w.greaterThanOrEqual.desktop} {
    ${casePathEmptyString('$singular')(css`
      flex-direction: row;
      max-width: 1120px;
      ${ContentContainer} {
        margin-left: 210px;
        ${dirRTL} {
          margin-left: 0;
          margin-right: 124px;
        }
      }
      ${ImageContainer} {
        min-width: 427px;
        height: 600px;
        margin-top: -238px;
      }
      ${ImageInnerContainer} {
        height: 616px;
      }
    `)}
  }
`;

const Description = styled(Paragraph)`
  text-transform: uppercase;
  margin-top: 20px;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
`;

export const ProductPageSctSectionItem = ({
  name,
  code,
  tagline,
  image,
  singular,
  description,
}: ProductPageSctSectionItemProps) => {
  const { _ } = useTranslate();

  const descriptionToDisplay = description ?? tagline;

  return (
    <Container $singular={singular}>
      {image && (
        <ImageContainer>
          <ImageInnerContainer>
            <PimImage
              src={image.id}
              layout="fill"
              objectFit="cover"
              sizes="100vw"
            />
          </ImageInnerContainer>
        </ImageContainer>
      )}

      <ContentContainer>
        <ProductLogo name={name} code={code} />

        {descriptionToDisplay && (
          <Description>
            <Markdown>{descriptionToDisplay}</Markdown>
          </Description>
        )}

        <TrackedLink href={route.products().page({ code })} passHref>
          <StyledButton forwardedAs="a" variant="secondary">
            {_('Discover')} {name}
          </StyledButton>
        </TrackedLink>
      </ContentContainer>
    </Container>
  );
};
